// Target: src/modules/billing/components/formSections/BillingSettingsFormSections.tsx
import type {
	Control,
	FieldErrors,
	FieldValues,
	Path,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from "react-hook-form";

import FormSection from "~/components/formElements/FormSection";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import BillingAccountingFormSection from "~/modules/billing/components/formSections/BillingAccountingFormSection.tsx";
import BillingCombineOrdersFormSection
	from "~/modules/billing/components/formSections/BillingCombineOrdersFormSection.tsx";
import BillingInstructionsFormSection
	from "~/modules/billing/components/formSections/BillingInstructionsFormSection.tsx";
import BillingInvoiceDeliveryFormSection
	from "~/modules/billing/components/formSections/BillingInvoiceDeliveryFormSection.tsx";
import BillingInvoiceRecipientFormSection
	from "~/modules/billing/components/formSections/BillingInvoiceRecipientFormSection.tsx";
import BillingInvoiceTypeFormSection from "~/modules/billing/components/formSections/BillingInvoiceTypeFormSection.tsx";
import type { BillingSettingsDivergingFields } from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import type { BillingSettingsBaseFormData } from "~/modules/billing/utils/billingSettingsFormUtils.ts";
import type { FormInputOption } from "~/types/form.ts";

type Props<T extends FieldValues & BillingSettingsBaseFormData> = {
	clientContactPersonSelectOptions: FormInputOption[];
	control: Control<T>;
	currencySelectOptions: FormInputOption[];
	errors: FieldErrors<T>;
	fieldsDivergingFromProject?: BillingSettingsDivergingFields;
	invoiceRecipientSelectOptions: FormInputOption[];
	isSubmitted: boolean;
	setValue: UseFormSetValue<T>;
	trigger: UseFormTrigger<T>;
	watch: UseFormWatch<T>;
	showCombineOrders?: boolean;
	showInvoiceType?: boolean;
};

const BillingSettingsFormSections = <T extends FieldValues & BillingSettingsBaseFormData>({
	clientContactPersonSelectOptions,
	control,
	currencySelectOptions,
	errors,
	fieldsDivergingFromProject,
	invoiceRecipientSelectOptions,
	isSubmitted,
	setValue,
	trigger,
	watch,
	showCombineOrders = false,
	showInvoiceType = true,
}: Props<T>) => {
	const currentInvoiceTypeId = watch("invoiceTypeId" as Path<T>);
	const showCustomPaymentTargetInput = watch("paymentTargetDays" as Path<T>) === 0;
	const showCurrencySelect = !!watch("isForeignCountry" as Path<T>);
	const isRegularInvoice = currentInvoiceTypeId === invoiceTypeId.RegularInvoice;

	return (
		<>
			{showInvoiceType && (
				<BillingInvoiceTypeFormSection
					control={control}
					fieldsDivergingFromProject={fieldsDivergingFromProject}
				/>
			)}

			<FormSection title={isRegularInvoice ? "Rechnungsempfänger" : "Ansprechpartner"}>
				<BillingInvoiceRecipientFormSection
					control={control}
					clientContactPersonSelectOptions={clientContactPersonSelectOptions}
					// @ts-expect-error root-level validation error path
					errorMessage={errors[""]?.message}
					fieldsDivergingFromProject={fieldsDivergingFromProject}
					invoiceRecipientSelectOptions={invoiceRecipientSelectOptions}
					isSubmitted={isSubmitted}
					setValue={setValue}
					trigger={trigger}
					watch={watch}

				/>
			</FormSection>
			{showCombineOrders && (
				<BillingCombineOrdersFormSection
					control={control as unknown as Control<FieldValues & { combineOrders: boolean }>}
				/>
			)}
			{isRegularInvoice && (
				<>
					<BillingAccountingFormSection
						control={control}
						currencySelectOptions={currencySelectOptions}
						fieldsDivergingFromProject={fieldsDivergingFromProject}
						errors={errors}
						showCurrencySelect={showCurrencySelect}
						showCustomPaymentTargetInput={showCustomPaymentTargetInput}
					/>

					<BillingInvoiceDeliveryFormSection
						control={control}
						fieldsDivergingFromProject={fieldsDivergingFromProject}
					/>
				</>
			)}
			<BillingInstructionsFormSection
				control={control}
			/>
		</>
	);
};

export default BillingSettingsFormSections;